import { useState, useMemo, useEffect } from 'react'
import { DateRange } from '@mui/x-date-pickers-pro'
import {
  DefaultHoursResponse,
  DefaultHoursParams,
} from '../../models/enhancement/default-department-hours'
import { v2MyActionsService } from '../../services/myActionsServiceV2'
import { RequestType } from '../../models'
import { formatLocalDateYmd } from '../date-utils'

interface UseDefaultHoursProps {
  employeeId: number
  departmentId: number
  teamId: number | undefined
  dateRange: DateRange<Date>
  type: RequestType
}

export const useDefaultHours = ({
  employeeId,
  departmentId,
  teamId,
  dateRange,
  type,
}: UseDefaultHoursProps) => {
  const [defaultHours, setDefaultHours] =
    useState<DefaultHoursResponse['defaultHours'] | null>(null)
  const [defaultCalculationCodes, setDefaultCalculationCodes] =
    useState<DefaultHoursResponse['defaultCalculationCodes'] | null>(null)
  const [calculationCodeOptions, setCalculationCodeOptions] =
    useState<DefaultHoursResponse['calculationCodeOptions'] | null>(null)

  const dates = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return []

    const days = []
    const currentDate = new Date(dateRange[0])
    while (currentDate <= dateRange[1]) {
      days.push(formatLocalDateYmd(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return days
  }, [dateRange])

  const getDefaultDepartmentHours = v2MyActionsService.getDefaultDepartmentOnCallHours

  useEffect(() => {
    const fetchDefaultHours = async () => {
      try {
        const bodyObject: DefaultHoursParams = { employeeId, departmentId, teamId, dates }
        const response = await getDefaultDepartmentHours(bodyObject)
        setDefaultHours(response.defaultHours)
        setDefaultCalculationCodes(response.defaultCalculationCodes ?? null)
        setCalculationCodeOptions(response.calculationCodeOptions ?? null)
      } catch (error) {
        console.error('Error fetching department hours:', error)
      }
    }

    const isOnCallType = type === RequestType.ON_CALL || type === RequestType.ON_CALL_M

    if (dates.length > 0 && isOnCallType) {
      fetchDefaultHours()
    }
  }, [dates, employeeId, departmentId, teamId, type, getDefaultDepartmentHours])

  const getDefaultEnhancementDayHours = useMemo(
    () =>
      (day: Date): number => {
        const isOnCallType = type === RequestType.ON_CALL || type === RequestType.ON_CALL_M

        if (!isOnCallType) {
          return 0
        }

        const dateKey = formatLocalDateYmd(day)
        const hoursFromAPI = defaultHours ? defaultHours[dateKey] : undefined
        return hoursFromAPI !== undefined ? hoursFromAPI : 0
      },
    [defaultHours, type]
  )

  const getDefaultEnhancementDayCode = useMemo(
    () =>
      (day: Date): string | null => {
        const isOnCallType = type === RequestType.ON_CALL || type === RequestType.ON_CALL_M

        if (!isOnCallType) {
          return null
        }

        const dateKey = formatLocalDateYmd(day)
        return defaultCalculationCodes ? defaultCalculationCodes[dateKey] ?? null : null
      },
    [defaultCalculationCodes, type]
  )

  const getValidCalculationCodes = useMemo(
    () =>
      (day: Date): string[] => {
        const isOnCallType = type === RequestType.ON_CALL || type === RequestType.ON_CALL_M

        if (!isOnCallType) {
          return []
        }

        const dateKey = formatLocalDateYmd(day)
        const options = calculationCodeOptions ? calculationCodeOptions[dateKey] : undefined
        return options
          ? [...options].sort((a, b) => Number(a) - Number(b))
          : []
      },
    [calculationCodeOptions, type]
  )

  return {
    defaultHours,
    defaultCalculationCodes,
    getDefaultEnhancementDayHours,
    getDefaultEnhancementDayCode,
    getValidCalculationCodes,
    dates,
  }
}
